// CampaignWorkflow.jsx
import React, { useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ReactFlow, {
  Background,
  BackgroundVariant,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';

import CustomNode from './CustomNode';
import { isTerminalWorkflowLabel, terminalWorkflowSublabel } from '../../components/workflowNodeSemantics';

// FontAwesome icons (same set as TemplateBuilder)
import { faCirclePlay } from '@fortawesome/free-solid-svg-icons';
import {
  faClock,
  faUsers,
  faEye,
  faCheck,
  faCommentDots,
  faCodeBranch,
} from '@fortawesome/free-solid-svg-icons';

const START_NODE_WIDTH = 200;

const LABEL_META = {
  Start: { icon: faCirclePlay, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  'Profile view': { icon: faEye, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  'Send invite': { icon: faUsers, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  Timer: { icon: faClock, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  Message: { icon: faCommentDots, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  Condition: { icon: faCodeBranch, iconClass: 'text-blue-600', badgeBg: 'bg-blue-100' },
  'Hand over to Agent': { icon: faCodeBranch, iconClass: 'text-purple-600', badgeBg: 'bg-purple-100' },
  'End flow': { icon: faCheck, iconClass: 'text-gray-600', badgeBg: 'bg-gray-100' },
};

const BRANCH_NODE_META = {
  Condition: {
    branches: [
      { id: 'true', label: 'Yes' },
      { id: 'false', label: 'No' },
    ],
  },
  'Send invite': {
    branches: [
      { id: 'accepted', label: 'Accepted' },
      { id: 'notAccepted', label: 'Not accepted' },
    ],
  },
  Message: {
    branches: [
      { id: 'response', label: 'Response' },
      { id: 'noResponse', label: 'No response' },
    ],
  },
};

const isBranchNodeLabel = (label) => !!BRANCH_NODE_META[label];

const getNodeSublabel = (nodeData) => {
  const label = nodeData?.label;

  if (label === 'Condition') {
    const ct = nodeData?.conditionType || 'isConnection';
    if (ct === 'isConnection') return 'IF: Connection exists';
    if (ct === 'hasLinkedinUrl') return 'IF: LinkedIn URL exists';
    if (ct === 'hasEmail') return 'IF: Email exists';
    return 'IF: Condition';
  }

  if (label === 'Send invite') return 'When invite is…';
  if (label === 'Message') return 'When lead…';
  return terminalWorkflowSublabel(label);
};

const countStatuses = (nodeTasks) => ({
  waiting: nodeTasks.filter((t) => t.status === 'waiting').length,
  pending: nodeTasks.filter((t) => t.status === 'pending').length,
  processing: nodeTasks.filter((t) => t.status === 'processing').length,
  completed: nodeTasks.filter((t) => t.status === 'completed').length,
  failed: nodeTasks.filter((t) => t.status === 'failed').length,
  canceled: nodeTasks.filter((t) => t.status === 'canceled').length,
});

const CampaignWorkflow = ({ campaign, tasks = [] }) => {
  const configured = campaign?.configuredWorkflow;
  const campaignId = campaign?._id;
  const workflowEditable = String(campaign?.status || '').toLowerCase() === 'concept';

  const nodeTypes = useMemo(() => ({ customNode: CustomNode }), []);

  const tasksByNodeId = useMemo(() => {
    const map = new Map();
    for (const t of tasks || []) {
      const key = t?.nodeId;
      if (!key) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return map;
  }, [tasks]);

  const totals = useMemo(() => countStatuses(tasks || []), [tasks]);

  const nodes = useMemo(() => {
    if (!configured?.nodes?.length) return [];

    return configured.nodes.map((n) => {
      const label = n?.data?.label || '';
      const meta = LABEL_META[label] || {};
      const isEndNode = isTerminalWorkflowLabel(label);
      const isBranchNode = isBranchNodeLabel(label);
      const nodeTasks = tasksByNodeId.get(n.id) || [];
      const taskCounts = countStatuses(nodeTasks);
      const subLabel = getNodeSublabel(n?.data);
      const isStart = n.id === 'start' || label === 'Start';
      const totalLeads = isStart
        ? (tasks || []).length
        : label === 'End flow'
          ? (tasks || []).filter((t) => t.status === 'completed').length
          : null;

      return {
        id: n.id,
        type: 'customNode',
        position: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
        data: {
          ...n.data,
          label,
          icon: n.data?.icon ?? meta.icon,
          iconClass: n.data?.iconClass ?? meta.iconClass,
          badgeBg: n.data?.badgeBg ?? meta.badgeBg,
          subLabel,
          isEndNode,
          isBranchNode,
          branches: isBranchNode ? BRANCH_NODE_META[label]?.branches || [] : [],
          taskCounts,
          totalLeads,
          workflowTotals: isStart ? totals : null,
        },
        className: n.className,
        draggable: false,
        selectable: true,
        deletable: false,
      };
    });
  }, [configured?.nodes, tasksByNodeId, tasks, totals]);

  const edges = useMemo(() => {
    if (!configured?.edges?.length) return [];

    return configured.edges.map((e, idx) => {
      const branch = e?.data?.branch || e?.sourceHandle || undefined;
      return {
        id: e.id || `edge_${e.source}_${e.target}_${idx}`,
        source: e.source,
        target: e.target,
        sourceHandle: e.sourceHandle,
        targetHandle: e.targetHandle,
        data: branch ? { branch } : e?.data,
        animated: false,
        style: { strokeWidth: 2 },
      };
    });
  }, [configured?.edges]);

  const { fitView, setCenter, getNodes, updateNodeInternals } = useReactFlow();

  const centerFlow = useCallback(() => {
    let tries = 0;

    const attempt = () => {
      const ns = getNodes?.() || [];
      if (!ns.length) return;

      const start = ns.find((x) => x.id === 'start' || x?.data?.label === 'Start');
      if (!start) {
        fitView({ padding: 0.2, includeHiddenNodes: false, duration: 250, minZoom: 0.55 });
        return;
      }

      updateNodeInternals?.(start.id);
      const w = start.width ?? START_NODE_WIDTH;
      const h = start.height ?? START_NODE_WIDTH;

      if ((!start.width || !start.height) && tries < 3) {
        tries += 1;
        requestAnimationFrame(attempt);
        return;
      }

      const isPortrait = window.innerHeight > window.innerWidth;
      if (isPortrait) {
        fitView({ nodes: [{ id: start.id }], padding: 0.6, duration: 180, minZoom: 0.55, maxZoom: 1 });
        setTimeout(() => {
          const cx = start.position.x + (start.width ?? w) / 2;
          const cy = start.position.y + (start.height ?? h) / 2 + 160;
          setCenter(cx, cy, { duration: 180 });
        }, 200);
      } else {
        const targetZoom = Math.min(0.95, Math.max(0.65, window.innerWidth < 900 ? 0.8 : 0.9));
        const cx = start.position.x + w / 2;
        const cy = start.position.y + h / 2 + 160;
        setCenter(cx, cy, { zoom: targetZoom, duration: 260 });
      }
    };

    requestAnimationFrame(attempt);
  }, [fitView, setCenter, getNodes, updateNodeInternals]);

  useEffect(() => {
    if (nodes.length) centerFlow();
  }, [nodes.length, centerFlow]);

  useEffect(() => {
    const onResize = () => centerFlow();
    window.addEventListener('orientationchange', onResize);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('orientationchange', onResize);
      window.removeEventListener('resize', onResize);
    };
  }, [centerFlow]);

  if (!configured) return <p>No workflow configured.</p>;

  return (
    <div className="reactflow-wrapper" style={{ width: '100%', height: '70vh', position: 'relative' }}>
      {campaignId && (
        <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 8 }}>
          {workflowEditable ? (
            <Link
              to={`/campaigns/${campaignId}/hybrid-flow`}
              className="btn btn-sm btn-outline-primary"
              title="Kies waar de vaste workflow wordt overgedragen aan de AI Agent"
            >
              Hybrid Flow / AI handoff
            </Link>
          ) : (
            <span className="badge bg-light text-secondary border">Workflow read-only</span>
          )}
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        zoomOnScroll={true}
        panOnScroll={false}
        panOnDrag={true}
        zoomOnDoubleClick={false}
        minZoom={0.6}
        maxZoom={1}
        deleteKeyCode={null}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#000" variant={BackgroundVariant.Dots} />
      </ReactFlow>
    </div>
  );
};

export default CampaignWorkflow;
